import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import CloseIcon from "assets/icons/close.svg?react";
import { useNavigate } from "react-router-dom";
import casesService from "api/casesService";
import { useEffect, useMemo, useState } from "react";
import sanitizeHtml from "sanitize-html";
import Chip from "components/Chip";
import { Avatar } from "@mui/material";
import { convertToLetters } from "utils/convertToLetters";
import { ITag } from "dto/ITag";
import tagsService from "api/tagsService";
import InputImitation from "components/InputImitation";
import SplitButton from "components/SplitButton";
import { ISplitButtonOption } from "components/SplitButton/typings";
import { useTranslation } from "react-i18next";

const PackageCaseModal: FC<Props> = ({ toggle, CaseId, Package }) => {
  const { t } = useTranslation();
  const { data: tagsData } = tagsService.useGetTags();
  const allTags = useMemo(() => tagsData?.data ?? [], [tagsData]);
  const [, setFindedTags] = useState<ITag[]>([]);
  const { data: CaseData } = casesService.useGetCase(CaseId ?? "");
  const Case = useMemo(() => CaseData, [CaseData]);

  const [caseData, setCaseData] = useState({ title: "", signature: "" });

  useEffect(() => {
    const t = allTags?.filter((tag: ITag) =>
      Case?.TagIds?.find((iTag) => tag.Id === iTag),
    );

    if (Case) {
      setCaseData({
        title: Case.Title,
        signature: Case.Signature,
      });
    }

    setFindedTags(t);
  }, [allTags, Case]);

  useEffect(() => {
    if (Package) {
      setCaseData({
        title: Package.Case.Title,
        signature: Package.Case.Signature,
      });
    }
  }, []);

  const navigate = useNavigate();

  const handleGotoCase = (option: ISplitButtonOption) => {
    switch (option.Id) {
      case "open":
        navigate(`/cases/${Case?.Id}/chat`);
        break;
      case "newTab":
        window.open(
          `${
            import.meta.env.VITE_APP_SITE ?? "https://alfa.pocztaprawnicza.pl"
          }/cases/${Case?.Id}/chat?fullscreen=1`,
        );
        break;
    }
    toggle(false);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.title}>
          {t("cases.case")}
          <CloseIcon
            className={styles.closeIcon}
            onClick={() => toggle(false)}
          />
        </div>
        <div className={styles.information}>
          <InputImitation value={sanitizeHtml(caseData.title)} label={t("forms.title")} />
          <InputImitation
            value={sanitizeHtml(
              caseData.signature ? caseData.signature : t("packageCaseModal.noData"),
            )}
            label={t("forms.signature")}
          />
        </div>

        <div className={styles.chips}>
          {CaseData?.Participants.map((p) => (
            <Chip
              avatar={
                <Avatar className={styles.avatar}>
                  {convertToLetters(p.Label ?? "")}
                </Avatar>
              }
              element={
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    gap: "5px",
                  }}
                >
                  {p.Label}
                </div>
              }
            />
          ))}
        </div>

        {CaseData && (
          <div className={styles.buttonWrapper}>
            <SplitButton
              className={styles.button}
              id={"gotoCase"}
              recoverDefaultOption={true}
              text={t("cases.goToCase")}
              options={[
                {
                  Id: "newTab",
                  Name: t("packageCaseModal.goToCaseNewTab"),
                },
                {
                  Id: "open",
                  Name: t("cases.goToCase"),
                },
              ]}
              onClick={handleGotoCase}
            ></SplitButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackageCaseModal;
